﻿import { useEffect, useRef, useState } from "react";
import { BrandLockup } from "../ui";
import { getFooterLiveStats } from "../../services/footerStatsRepository";

const productLinks = [
  { label: "Ana Sayfa", path: "/" },
  { label: "Etkinlik Keşfet", path: "/kesfet" },
  { label: "Etkinlik Oluştur", path: "/etkinlik-olustur" },
  { label: "Harita", path: "/harita" },
  { label: "Profilim", path: "/profilim" },
];

const companyLinks = [
  { label: "Hakkımızda", path: "/hakkimizda" },
  { label: "Topluluk İlkeleri", path: "/topluluk-ilkeleri" },
  { label: "Moderatörlük", path: "/moderatorluk" },
  { label: "Partnerlik", path: "/partnerlik" },
  { label: "Sosyal Medya", path: "/sosyal-medya" },
];

const legalLinks = [
  { label: "Privacy Policy", path: "/privacy" },
  { label: "Terms of Service", path: "/terms" },
  { label: "Temel Kullanım", path: "/yasal#temel-kullanim" },
  { label: "Gizlilik ve KVKK", path: "/yasal#gizlilik-kvkk" },
  { label: "Etkinlik ve Organizasyon", path: "/yasal#etkinlik-organizasyon" },
  { label: "Güvenlik ve Moderasyon", path: "/yasal#guvenlik-moderasyon" },
  { label: "Ticari, Ödeme ve Reklam", path: "/yasal#ticari-odeme" },
];

function formatMetric(value) {
  if (!Number.isFinite(value)) return "0";
  return new Intl.NumberFormat("tr-TR").format(Math.max(0, Math.floor(value)));
}

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function getPublicUserBand(baseUsers) {
  if (baseUsers <= 0) return { min: 0, max: 0 };
  return {
    min: Math.max(0, baseUsers - 10),
    max: baseUsers + 10,
  };
}

function getInitialPublicUserCount(baseUsers) {
  const band = getPublicUserBand(baseUsers);
  if (band.max === 0) return 0;
  return clampNumber(baseUsers + randomInt(-3, 3), band.min, band.max);
}

function getNextPublicUserTarget(baseUsers, currentValue) {
  const band = getPublicUserBand(baseUsers);
  if (band.max === 0) return 0;

  const outsideBand = currentValue < band.min || currentValue > band.max;
  const jitter = outsideBand ? randomInt(-3, 3) : randomInt(-10, 10);
  return clampNumber(baseUsers + jitter, band.min, band.max);
}

function getPublicUserStep(diff) {
  const distance = Math.abs(diff);
  if (distance <= 1) return distance;
  if (distance < 8) return randomInt(1, 2);
  if (distance < 24) return randomInt(2, 4);
  return randomInt(3, 6);
}

function getNextPublicCounterDelay() {
  return randomInt(10_000, 30_000);
}

function AnimatedFooterMetricValue({ value }) {
  const numericValue = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
  const previousValueRef = useRef(numericValue);
  const [animationState, setAnimationState] = useState({ key: 0, direction: "stable" });

  useEffect(() => {
    const previousValue = previousValueRef.current;
    if (numericValue === previousValue) return;

    previousValueRef.current = numericValue;
    setAnimationState((current) => ({
      key: current.key + 1,
      direction: numericValue > previousValue ? "up" : "down",
    }));
  }, [numericValue]);

  const directionClass =
    animationState.direction === "up" || animationState.direction === "down"
      ? ` site-footer-metric-value--${animationState.direction}`
      : "";

  return (
    <strong>
      <span key={animationState.key} className={`site-footer-metric-value${directionClass}`}>
        {formatMetric(numericValue)}
      </span>
    </strong>
  );
}

export default function SiteFooter({ onNavigate }) {
  const [stats, setStats] = useState({ activeEventCount: 0, activeUsers: 0 });
  const [publicActiveUsers, setPublicActiveUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusError, setStatusError] = useState("");
  const activeUsersBaseRef = useRef(0);
  const publicUsersTargetRef = useRef(0);
  const nextPublicRetargetAtRef = useRef(0);
  const publicCounterReadyRef = useRef(false);

  useEffect(() => {
    let alive = true;

    const loadStats = async () => {
      try {
        const next = await getFooterLiveStats();
        if (!alive) return;
        setStatusError("");
        const activeUsers = Math.max(0, Math.floor(Number(next?.activeUsers || 0)));
        activeUsersBaseRef.current = activeUsers;
        setStats({
          activeEventCount: Number(next?.activeEventCount || 0),
          activeUsers,
        });
        setPublicActiveUsers((currentValue) => {
          if (!publicCounterReadyRef.current) {
            const initialValue = getInitialPublicUserCount(activeUsers);
            publicCounterReadyRef.current = true;
            publicUsersTargetRef.current = getNextPublicUserTarget(activeUsers, initialValue);
            nextPublicRetargetAtRef.current = Date.now() + getNextPublicCounterDelay();
            return initialValue;
          }

          publicUsersTargetRef.current = getNextPublicUserTarget(activeUsers, currentValue);
          nextPublicRetargetAtRef.current = Date.now() + getNextPublicCounterDelay();
          return currentValue;
        });
      } catch (error) {
        if (!alive) return;
        const missingKeys = Array.isArray(error?.missingKeys) ? error.missingKeys.join(", ") : "";
        setStatusError(missingKeys ? `Firebase bağlantısı eksik: ${missingKeys}` : "Canlı sayaçlar gerçek veriden yüklenemedi.");
      } finally {
        if (alive) setLoading(false);
      }
    };

    loadStats();
    const timer = window.setInterval(loadStats, 60_000);

    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    let timer = 0;

    const updatePublicCounter = () => {
      setPublicActiveUsers((currentValue) => {
        const baseUsers = activeUsersBaseRef.current;
        const now = Date.now();

        if (now >= nextPublicRetargetAtRef.current || currentValue === publicUsersTargetRef.current) {
          publicUsersTargetRef.current = getNextPublicUserTarget(baseUsers, currentValue);
          nextPublicRetargetAtRef.current = now + getNextPublicCounterDelay();
        }

        const targetValue = publicUsersTargetRef.current;
        const diff = targetValue - currentValue;
        if (diff === 0) return currentValue;

        const direction = Math.sign(diff);
        const step = Math.min(Math.abs(diff), getPublicUserStep(diff));
        return Math.max(0, currentValue + direction * step);
      });

      if (alive) {
        timer = window.setTimeout(updatePublicCounter, getNextPublicCounterDelay());
      }
    };

    timer = window.setTimeout(updatePublicCounter, getNextPublicCounterDelay());

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, []);

  const handleNavigate = (event, path) => {
    event.preventDefault();
    onNavigate(path);
  };

  const displayedPublicActiveUsers = loading || statusError ? publicActiveUsers : Math.max(1, publicActiveUsers);

  return (
    <footer className="site-footer">
      <div className="site-footer__hero">
        <BrandLockup />
      </div>

      <div className="site-footer__columns">
        <div className="site-footer__column">
          <h4>Ürün</h4>
          {productLinks.map((link) => (
            <a href={link.path} key={link.path} onClick={(event) => handleNavigate(event, link.path)}>
              {link.label}
            </a>
          ))}
        </div>

        <div className="site-footer__column">
          <h4>Kurumsal</h4>
          {companyLinks.map((link) => (
            <a href={link.path} key={link.path} onClick={(event) => handleNavigate(event, link.path)}>
              {link.label}
            </a>
          ))}
        </div>

        <div className="site-footer__column">
          <h4>Yasal</h4>
          {legalLinks.map((link) => (
            <a href={link.path} key={link.path} onClick={(event) => handleNavigate(event, link.path)}>
              {link.label}
            </a>
          ))}
        </div>

        <div className="site-footer__column site-footer__column--status">
          <h4>Durum</h4>
          <div className="site-footer-status-grid">
            <div className="site-footer-status-item">
              <span>Aktif Etkinlik Sayısı</span>
              {statusError ? <strong>Bağlantı yok</strong> : loading ? <strong>Yükleniyor...</strong> : <AnimatedFooterMetricValue value={stats.activeEventCount} />}
            </div>
            <div className="site-footer-status-item">
              <span>Aktif Kullanıcı</span>
              {statusError ? <strong>Bağlantı yok</strong> : loading ? <strong>Yükleniyor...</strong> : <AnimatedFooterMetricValue value={displayedPublicActiveUsers} />}
            </div>
          </div>
        </div>
      </div>

      <div className="site-footer__bottom">
        <span>© {new Date().getFullYear()} DenkUP. Tüm hakları saklıdır.</span>
        <span>VersionNo: 0.1.0.4</span>
      </div>
    </footer>
  );
}

